import {gql, QueryResult} from '@apollo/client';
import {Group, Button, Stack, Box, createStyles, Text, Title, Paper} from '@mantine/core';
import {DocumentNode} from 'graphql';
import React, {useEffect} from 'react';

import * as OrganizationActions from 'actions/OrganizationActions';
import * as analytics from 'analytics/Analytics';
import * as AuditActions from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import {setWindowTitle} from 'BenchlingUtil';
import {IDENTITY_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import BenchlingQuery, {CachedDataWhenRefetchingNamedOption} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {useRolloutFlags} from 'launchdarkly/LaunchDarklyUtil';
import navigateToURL from 'lib/navigateToURL';
import notifications, {Response} from 'notifications';
import UserDataAccessTab from 'orgs/UserDataAccessTab';
import {SuspendUserConfirmationModal} from 'tenants/admin/SuspendUserConfirmationModal';
import TenantBreadcrumbs from 'tenants/TenantBreadcrumbs';
import Avatar from 'ui-library/avatar/Avatar';
import {AvatarShape, AvatarSize} from 'ui-library/avatar/AvatarConstants';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import legacyRunModal from 'ui-library/legacyRunModal';
import SectionTabs from 'ui-library/SectionTabs';
import fuzzydate from 'util/fuzzydate';
import {callV3Internal} from 'v3-internal-client';

import {UserPageQuery, UserPageQueryVariables} from './__generated/UserPageQuery';
import UserBasicSettingsTab, {USER_BASIC_SETTINGS_TAB_USER_FRAGMENT} from './UserBasicSettingsTab';

type UserPageQueryUser = ExtractByTypename<UserPageQuery['user'], 'UserNode'>;

export function navigateToUserPage(userId: string): void {
  analytics.trackEvent(IDENTITY_ANALYTICS_ACTIONS.CLICKED_PARTY_SETTINGS, {
    objectType: 'user',
    objectId: userId,
  });
  navigateToURL(`users/${userId}`);
}

export enum UserSettingTab {
  SETTINGS_TAB = 'SETTINGS_TAB',
  ACCESS_TAB = 'ACCESS_TAB',
}

const TAB_TO_DISPLAY: {[key in UserSettingTab]: string} = {
  [UserSettingTab.SETTINGS_TAB]: 'Basic Settings',
  [UserSettingTab.ACCESS_TAB]: 'Access',
};

export const USER_PAGE_QUERY: DocumentNode = gql`
  query UserPageQuery($userId: StrictString!) @skipAudit @engComponent(name: IDENTITY) {
    user(id: $userId) {
      ... on UserNode {
        id
        ...UserBasicSettingsTabUser
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${USER_BASIC_SETTINGS_TAB_USER_FRAGMENT}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export type RefetchUserPage = QueryResult<UserPageQuery, UserPageQueryVariables>['refetch'];

interface UserPageProps {
  user: UserPageQueryUser;
  activeTab: UserSettingTab;
  refetch: RefetchUserPage;
}

interface UserHeaderProps {
  user: UserPageQueryUser;
  refetch: RefetchUserPage;
}

export interface QueryingUserPageProps {
  userId: string;
  activeTab: string;
}

const useStyles = createStyles((theme) => ({
  exportButton: {
    backgroundColor: theme.colors.gray[1],
    color: theme.colors.gray[8],
    '&:hover': {
      backgroundColor: theme.colors.gray[2],
      color: theme.colors.gray[9],
    },
  },
  container: {
    height: '100%',
  },
  pageGutter: {
    paddingInline: 'calc(100% / 22)',
  },
}));

function UserHeader({user, refetch}: UserHeaderProps): JSX.Element {
  const {classes} = useStyles();
  const {partiesEnableNewUserProfileTacPage} = useRolloutFlags();

  const handleToggleSuspend = (): void => {
    const suspending = !user.suspended;

    const applySuspendedStatus = async (): Promise<void> => {
      await callV3Internal('User.Update', {
        pathParams: {user_id: user.id},
        body: {status: suspending ? 'SUSPENDED' : 'ACTIVE'},
      });
      await refetch();
      notifications.success(`Successfully ${suspending ? 'suspended' : 'unsuspended'} user.`);
    };

    const persistSuspended = (): Promise<void> =>
      applySuspendedStatus().catch((res: Response) => {
        notifications.notifyError(res);
      });

    if (suspending) {
      legacyRunModal(SuspendUserConfirmationModal, {
        suspendMultiple: false,
        onSuspend: persistSuspended,
      });
    } else {
      persistSuspended();
    }
  };

  return (
    <Group position="apart" py="sm">
      <Group>
        <Avatar size={AvatarSize.LARGE} shape={AvatarShape.SQUARE} entity={user} />
        <Stack spacing="xs">
          <Group>
            <Title order={1} size="h3">
              {user.name}
            </Title>
            <Paper radius="sm" py="xs" px="sm" bg={user.suspended ? 'gray.1' : 'green.2'}>
              {user.suspended ? 'Suspended' : 'Active'}
            </Paper>
          </Group>
          <Text>Last Seen: {user.lastSeen ? fuzzydate(user.lastSeen) : 'Never logged in'}</Text>
        </Stack>
      </Group>
      {partiesEnableNewUserProfileTacPage && (
        <Group>
          <Button
            color={user.suspended ? 'blue' : 'red'}
            variant={user.suspended ? 'white' : 'filled'}
            onClick={handleToggleSuspend}
          >
            {user.suspended ? 'Unsuspend' : 'Suspend'}
          </Button>
          <Button
            className={classes.exportButton}
            onClick={() => {
              AuditActions.exportAuditLogs([user.id]);
            }}
          >
            {EXPORT_AUDIT_LOG_DISPLAY_STRING}
          </Button>
        </Group>
      )}
    </Group>
  );
}

function UserPage(props: UserPageProps): JSX.Element {
  const {classes} = useStyles();
  useEffect(() => {
    setWindowTitle(props.user.name);
  }, [props.user.name]);
  return (
    <Box className={classes.container} pt="xl">
      <Box className={classes.pageGutter}>
        <TenantBreadcrumbs />
        <UserHeader user={props.user} refetch={props.refetch} />
        <SectionTabs
          tabs={Object.keys(TAB_TO_DISPLAY).map((userSettingTab: UserSettingTab) => ({
            handleClick: () => {
              OrganizationActions.updateActiveTab(props.user.id, userSettingTab);
            },
            ariaControls: `${userSettingTab}_PANEL`,
            id: userSettingTab,
            text: TAB_TO_DISPLAY[userSettingTab],
            isActive: props.activeTab === userSettingTab,
          }))}
        />
      </Box>
      <Box
        aria-labelledby={props.activeTab}
        className="organizationPage-sectionContainer"
        id={`${props.activeTab}_PANEL`}
        role="tabpanel"
      >
        {(() => {
          switch (props.activeTab) {
            case UserSettingTab.SETTINGS_TAB:
              return <UserBasicSettingsTab user={props.user} refetch={props.refetch} />;
            case UserSettingTab.ACCESS_TAB:
              return <UserDataAccessTab userId={props.user.id} />;
            default:
              return null;
          }
        })()}
      </Box>
    </Box>
  );
}

export default function QueryingUserPage(props: QueryingUserPageProps): JSX.Element {
  return (
    <BenchlingQuery<UserPageQuery, UserPageQueryVariables>
      query={USER_PAGE_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      // Keep the page mounted while refetching so in-progress field edits aren't wiped by a remount.
      renderCachedDataWhenRefetching={CachedDataWhenRefetchingNamedOption.WHEN_LOADING}
      variables={{userId: props.userId}}
    >
      {({user}, {refetch}) => {
        if (isInaccessible(user)) {
          return (
            <div>
              <div className="u-gray-darker">Inaccessible user id</div>
            </div>
          );
        }
        return (
          <UserPage
            user={user}
            activeTab={
              UserSettingTab[props.activeTab as keyof typeof UserSettingTab] ?? UserSettingTab.SETTINGS_TAB
            }
            refetch={refetch}
          />
        );
      }}
    </BenchlingQuery>
  );
}
